'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';

function MomentBadge() {
  return (
    <svg width="28" height="15" viewBox="0 0 28 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline ml-1 mb-0.5 h-[7px] w-auto" aria-label="Moment">
      <path d="M16 4.09091H19.5V2.72727H23.5V0H28V6.81818H25.2V9.54545H22.4V10.9091H20V13.6364H15.4V15H12V11H8.5V13H4.2V15H0V9.54545H2.8V6H7V2.72727H11.2V0H16V4.09091Z" fill="currentColor"/>
    </svg>
  );
}

interface UserSubmission {
  id: string;
  status: string;
  score: number | null;
  foodTotal: number | null;
  foodCollected: number | null;
  error: string | null;
  submittedAt: string;
  code: string;
}

interface LeaderboardEntry {
  id: string;
  handle: string;
  score: number;
  rank: number;
  isMoment: boolean;
  submissions: number;
}

interface SwarmLeaderboardTabProps {
  user: { githubUsername: string } | null;
  onLoadCode: (code: string) => void;
  scoringSubmission?: { id: string; code: string; submittedAt: string } | null;
  challengeEndDate?: string;
}

function relativeTime(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SwarmLeaderboardTab({ user, onLoadCode, scoringSubmission, challengeEndDate }: SwarmLeaderboardTabProps) {
  const competitionEnded = challengeEndDate ? new Date() > new Date(challengeEndDate) : false;
  const [boardMode, setBoardMode] = useState<'prize' | 'freeplay'>(competitionEnded ? 'prize' : 'freeplay');
  const [userSubs, setUserSubs] = useState<UserSubmission[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userContext, setUserContext] = useState<LeaderboardEntry[] | null>(null);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingBoard, setLoadingBoard] = useState(true);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const url = boardMode === 'prize' ? '/api/leaderboard?mode=prize' : '/api/leaderboard';
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.top ?? data);
        setUserContext(data.userContext ?? null);
        setTotalSubmissions(data.totalSubmissions ?? 0);
      }
    } catch { /* ignore */ }
  }, [boardMode]);

  useEffect(() => {
    setLoadingBoard(true);
    fetchLeaderboard().then(() => setLoadingBoard(false));
    const interval = setInterval(fetchLeaderboard, 10_000);
    return () => clearInterval(interval);
  }, [fetchLeaderboard]);

  const fetchUserSubs = useCallback(async () => {
    if (!user) { setUserSubs([]); return; }
    const res = await fetch('/api/swarm/submissions').catch(() => null);
    if (res?.ok) setUserSubs(await res.json());
  }, [user]);

  useEffect(() => {
    setLoadingUser(true);
    fetchUserSubs().finally(() => setLoadingUser(false));
  }, [fetchUserSubs]);

  useEffect(() => {
    if (!scoringSubmission) return;
    const interval = setInterval(fetchUserSubs, 5_000);
    return () => clearInterval(interval);
  }, [scoringSubmission, fetchUserSubs]);

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code);
  }, []);

  const displaySubs = useMemo(() => {
    if (!scoringSubmission || userSubs.some(s => s.id === scoringSubmission.id)) return userSubs;
    const optimistic: UserSubmission = {
      id: scoringSubmission.id,
      status: 'scoring',
      score: null,
      foodTotal: null,
      foodCollected: null,
      error: null,
      submittedAt: scoringSubmission.submittedAt,
      code: scoringSubmission.code,
    };
    return [optimistic, ...userSubs];
  }, [scoringSubmission, userSubs]);

  const maxScore = leaderboard[0]?.score || 1000;

  return (
    <div className="flex-1 overflow-y-auto p-4 text-[11px] min-h-0">
      {user && (
        <div className="mb-6">
          <h3
            className="text-amber font-bold text-[10px] tracking-widest uppercase mb-2"
          >
            Your Submissions
          </h3>

          {loadingUser ? (
            <span className="text-dim">Loading...</span>
          ) : displaySubs.length === 0 ? (
            <span className="text-dim">No submissions yet</span>
          ) : (
            <div className="space-y-px">
              {displaySubs.map(sub => {
                const expanded = expandedId === sub.id;
                const confirming = confirmId === sub.id;
                return (
                  <div key={sub.id}>
                    <div className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-elevated/50 transition-colors">
                      <StatusDot status={sub.status} />
                      <span className={`w-16 tabular-nums ${
                        sub.status === 'completed' ? 'text-amber font-medium'
                        : sub.status === 'failed' || sub.status === 'cancelled' ? 'text-red'
                        : 'text-secondary'
                      }`}>
                        {sub.status === 'completed' && sub.score !== null
                          ? `${sub.score}/1000`
                          : sub.status === 'scoring' ? 'scoring...'
                          : sub.status === 'failed' ? 'failed'
                          : sub.status === 'cancelled' ? 'cancelled'
                          : 'pending'}
                      </span>
                      <span className="text-dim flex-1">{relativeTime(sub.submittedAt)}</span>
                      <button
                        onClick={() => setExpandedId(expanded ? null : sub.id)}
                        className={`px-1.5 py-0.5 rounded border text-[9px] cursor-pointer transition-colors ${
                          expanded
                            ? 'border-cyan text-cyan bg-elevated'
                            : 'border-stroke/50 text-dim hover:text-primary hover:border-stroke'
                        }`}
                      >
                        {expanded ? 'hide' : 'view'}
                      </button>
                      {sub.status !== 'scoring' && sub.status !== 'pending' && sub.status !== 'cancelled' && (
                        <button
                          onClick={() => setConfirmId(confirming ? null : sub.id)}
                          className="px-1.5 py-0.5 rounded border border-stroke/50 text-dim hover:text-amber hover:border-amber/40 text-[9px] cursor-pointer transition-colors"
                        >
                          load
                        </button>
                      )}
                    </div>

                    {confirming && (
                      <div className="mx-2 mb-1 px-2 py-1.5 bg-elevated rounded border border-stroke/50 flex items-center gap-2">
                        <span className="text-secondary text-[10px]">Replace current code?</span>
                        <button
                          onClick={() => { onLoadCode(sub.code); setConfirmId(null); }}
                          className="px-1.5 py-0.5 rounded border border-amber/40 text-amber text-[9px] cursor-pointer hover:bg-amber/10 transition-colors"
                        >
                          yes
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="px-1.5 py-0.5 rounded border border-stroke/50 text-dim text-[9px] cursor-pointer hover:text-primary transition-colors"
                        >
                          no
                        </button>
                      </div>
                    )}

                    {expanded && (
                      <div className="mx-2 mb-2 bg-surface rounded border border-stroke/50 overflow-hidden">
                        <pre className="p-2 text-[10px] leading-[1.6] text-secondary max-h-48 overflow-y-auto whitespace-pre">
                          {sub.code}
                        </pre>
                        <div className="flex items-center gap-1 px-2 py-1 border-t border-stroke/30">
                          <button
                            onClick={() => { setConfirmId(sub.id); }}
                            className="px-1.5 py-0.5 rounded border border-amber/40 text-amber text-[9px] cursor-pointer hover:bg-amber/10 transition-colors"
                          >
                            load into editor
                          </button>
                          <button
                            onClick={() => handleCopy(sub.code)}
                            className="px-1.5 py-0.5 rounded border border-stroke/50 text-dim hover:text-primary text-[9px] cursor-pointer transition-colors"
                          >
                            copy
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          {competitionEnded ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setBoardMode('prize')}
                className={`px-2 py-0.5 rounded text-[10px] tracking-widest uppercase font-bold cursor-pointer transition-colors ${
                  boardMode === 'prize' ? 'text-amber bg-amber/10' : 'text-dim hover:text-primary'
                }`}
              >
                Final Standings
              </button>
              <button
                onClick={() => setBoardMode('freeplay')}
                className={`px-2 py-0.5 rounded text-[10px] tracking-widest uppercase font-bold cursor-pointer transition-colors ${
                  boardMode === 'freeplay' ? 'text-cyan bg-cyan/10' : 'text-dim hover:text-primary'
                }`}
              >
                Free Play
              </button>
            </div>
          ) : (
            <h3 className="text-amber font-bold text-[10px] tracking-widest uppercase">
              Leaderboard
            </h3>
          )}
          {totalSubmissions > 0 && (
            <span className="text-dim text-[9px]">{totalSubmissions} submissions</span>
          )}
        </div>

        {loadingBoard ? (
          <span className="text-dim">Loading...</span>
        ) : leaderboard.length === 0 ? (
          <span className="text-dim">No submissions yet</span>
        ) : (
          <div className="space-y-px">
            {leaderboard.map((entry) => (
              <LeaderboardRow key={entry.id} entry={entry} isUser={!!user && entry.handle === user.githubUsername} maxScore={maxScore} />
            ))}
            {userContext && (
              <>
                {userContext[0]?.rank > leaderboard.length + 1 && (
                  <div className="flex items-center gap-2 px-2 py-1 text-dim text-[10px]">
                    <span className="w-5 text-right">···</span>
                  </div>
                )}
                {userContext.map((entry) => (
                  <LeaderboardRow key={entry.id} entry={entry} isUser={!!user && entry.handle === user.githubUsername} maxScore={maxScore} />
                ))}
              </>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

function LeaderboardRow({ entry, isUser, maxScore }: { entry: LeaderboardEntry; isUser: boolean; maxScore: number }) {
  const isMoment = entry.rank === 0;
  const isTop3 = !isMoment && entry.rank <= 3;
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-elevated/50 transition-colors ${
        isUser ? 'bg-cyan/5' : ''
      }`}
    >
      <span className={`w-5 text-right ${isMoment ? 'text-secondary' : isTop3 ? 'text-amber font-bold' : 'text-dim'}`}>
        {isMoment ? <MomentBadge /> : String(entry.rank).padStart(2, '0')}
      </span>
      <span className={`flex-1 truncate ${isUser ? 'text-cyan font-medium' : 'text-primary'}`}>
        {entry.handle}
      </span>
      <span className={`w-12 text-right font-medium tabular-nums ${isTop3 ? 'text-amber' : 'text-primary'}`}>
        {entry.score}
      </span>
      <div className="w-12 h-1 bg-stroke rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${isTop3 ? 'bg-amber' : 'bg-stroke-bright'}`}
          style={{ width: `${(entry.score / maxScore) * 100}%` }}
        />
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  if (status === 'completed') {
    return <span className="w-1.5 h-1.5 rounded-full bg-green shrink-0" />;
  }
  if (status === 'scoring') {
    return (
      <span
        className="w-1.5 h-1.5 rounded-full bg-amber shrink-0"
        style={{ animation: 'pulse-dot 1.5s ease-in-out infinite' }}
      />
    );
  }
  if (status === 'failed' || status === 'cancelled') {
    return <span className="w-1.5 h-1.5 rounded-full bg-red shrink-0" />;
  }
  return <span className="w-1.5 h-1.5 rounded-full bg-dim shrink-0" />;
}
